/**
 * Which Forge Cloud rows a sidebar row stands for, and how each of them names
 * itself to the live catalog.
 *
 * Split out of `sidebarProjectRemoval.ts` under the file-size ratchet. It is
 * route CONSTRUCTION only: nothing here reads the catalog, decides a plan, or
 * produces a `CloudRemovalTarget`. Those stay in the removal module beside
 * `resolveCloudRemovalPlan`, which is the one place allowed to decide what a
 * removal may remove.
 */
import type { EnvironmentId } from "@cmmd-forge/contracts";
import { classifyEnvironmentId } from "../../environments/environmentIdClassification";
import type { SidebarProjectSnapshot } from "./SidebarProjectItem";

export type RemovableForgeProject = Pick<
  SidebarProjectSnapshot,
  | "environmentId"
  | "id"
  | "memberProjectRefs"
  | "worktreeMembers"
  | "preConnectCloud"
  | "cloudRegistrationProjectId"
>;

/**
 * One cloud member of a sidebar row, with the locators it can offer.
 *
 * Two of them, because a cloud Environment reaches the sidebar under two keys.
 * The legacy `cmmd-runtime:<id>` locator carries its runtime in the id itself;
 * CMMD's durable Environment key (`env_…`, FOR-32652) carries none, and is
 * matched against the `environmentId` on a catalog row's runtime overlay
 * instead — the same field `findCloudRowForEnvironment` follows when a route
 * attaches. A row keyed by an id sets exactly one of them; a pre-connect row
 * carries whatever its catalog metadata knows, which may be both or neither,
 * and is decided by its registration id anyway. `routeLocatorIdentity` is
 * where one of the two is chosen, so a route is never asked to be a pair.
 */
export interface CloudRemovalRoute {
  readonly environmentId: EnvironmentId;
  readonly runtimeId: string | null;
  readonly cmmdEnvironmentId: string | null;
}

function cloudRemovalRouteForEnvironment(environmentId: EnvironmentId): CloudRemovalRoute | null {
  const { legacyRuntimeId, shape } = classifyEnvironmentId(environmentId);
  if (legacyRuntimeId !== null) {
    return { environmentId, runtimeId: legacyRuntimeId, cmmdEnvironmentId: null };
  }
  // A durable Environment is a cloud row that simply keeps its runtime
  // elsewhere. Returning null here is what left it with nothing to say to the
  // catalog, so delete-files on a connected durable row refused outright.
  return shape === "durable-environment"
    ? { environmentId, runtimeId: null, cmmdEnvironmentId: String(environmentId) }
    : null;
}

function dedupeCloudRemovalRoutes(
  candidates: readonly (CloudRemovalRoute | null)[],
): readonly CloudRemovalRoute[] {
  return [
    ...new Map(
      candidates.flatMap((route) =>
        route
          ? ([
              [
                `${route.environmentId}:${route.runtimeId ?? route.cmmdEnvironmentId ?? "catalog"}`,
                route,
              ],
            ] as const)
          : [],
      ),
    ).values(),
  ];
}

/**
 * Resolve every cloud execution member represented by a sidebar row. A mixed
 * project keeps one representative for routing and display, but removal is a
 * logical-project action: every distinct Forge Cloud member must be removed.
 *
 * A plain local row still returns an empty list even when it carries a
 * cwd-based CMMD planning-project link; that link is not a Forge Cloud
 * registration.
 */
export function resolveCloudRemovalRoutes(
  project: RemovableForgeProject,
): readonly CloudRemovalRoute[] {
  const representativeRoute = cloudRemovalRouteForEnvironment(project.environmentId);
  if (project.preConnectCloud) {
    return [
      {
        environmentId: project.environmentId,
        runtimeId: project.preConnectCloud.runtimeId ?? representativeRoute?.runtimeId ?? null,
        cmmdEnvironmentId:
          project.preConnectCloud.cmmdEnvironmentId ??
          representativeRoute?.cmmdEnvironmentId ??
          null,
      },
    ];
  }

  return dedupeCloudRemovalRoutes([
    representativeRoute,
    ...(project.worktreeMembers ?? [])
      .filter((member) => member.environmentRole === "cloud")
      .map((member) => cloudRemovalRouteForEnvironment(member.environmentId)),
    ...project.memberProjectRefs.map((memberRef) =>
      cloudRemovalRouteForEnvironment(memberRef.environmentId),
    ),
  ]);
}
